import React, { useState } from 'react';
import { Modal, Select, message } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import {
    BATCH_UPDATE_DISPATCH_EVENT_TASK_ORDER_STATUS,
    BatchUpdateDispatchEventTaskOrderStatusResponse,
} from '../graphql/mutations/dispatchToolMutations';

/** 更新目标：task=按调度任务更新其下全部明细行；order=只更新单条明细行；orders=更新勾选的多条明细行 */
export type OrderFieldUpdateTarget =
    | { kind: 'task'; dispatchEventTaskId: number }
    | { kind: 'order'; orderRowId: number }
    | { kind: 'orders'; orderRowIds: number[] };

type OrderUpdateField = 'isCompleted' | 'isSentMq' | 'retryCount';

const ORDER_UPDATE_FIELD_OPTIONS = [
    { value: 'isCompleted' as const, label: 'is_completed（是否完成）' },
    { value: 'isSentMq' as const, label: 'is_sent_mq（是否发MQ）' },
    { value: 'retryCount' as const, label: 'retry_count（重试次数）' },
];

const FLAG_OPTIONS = [
    { value: 0, label: '0（否）' },
    { value: 1, label: '1（是）' },
];

// retry_count 目标值 0~5；重置为 0 可让执行 Job 重新捞起（仍受 48h 窗口限制）
const RETRY_VALUE_OPTIONS = Array.from({ length: 6 }, (_, i) => ({ value: i, label: String(i) }));

type Props = {
    target: OrderFieldUpdateTarget | null;
    onClose: () => void;
    /** 更新成功后回调（如刷新列表） */
    onSuccess?: () => void;
};

const OrderFieldUpdateModal: React.FC<Props> = ({ target, onClose, onSuccess }) => {
    const [updateField, setUpdateField] = useState<OrderUpdateField | undefined>(undefined);
    const [targetValue, setTargetValue] = useState<number | undefined>(undefined);
    const [runOrderUpdate, { loading }] = useMutation<BatchUpdateDispatchEventTaskOrderStatusResponse>(
        BATCH_UPDATE_DISPATCH_EVENT_TASK_ORDER_STATUS,
        { onError: (e) => message.error(e.message) },
    );

    const scopeText = target === null
        ? ''
        : target.kind === 'task'
            ? `调度任务 ${target.dispatchEventTaskId} 下全部订单明细`
            : target.kind === 'order'
                ? `明细记录 ${target.orderRowId}（仅此一条）`
                : `已勾选的 ${target.orderRowIds.length} 条明细记录`;

    const titleText = target === null
        ? '更新字段'
        : target.kind === 'task'
            ? `更新字段（调度任务ID ${target.dispatchEventTaskId}）`
            : target.kind === 'order'
                ? `更新字段（明细ID ${target.orderRowId}）`
                : `更新字段（已选 ${target.orderRowIds.length} 条）`;

    const close = () => {
        setUpdateField(undefined);
        setTargetValue(undefined);
        onClose();
    };

    const doUpdate = async (field: OrderUpdateField, value: number) => {
        if (!target) return;
        const result = await runOrderUpdate({
            variables: {
                input: {
                    dispatchEventTaskId: target.kind === 'task' ? target.dispatchEventTaskId : undefined,
                    ids: target.kind === 'order'
                        ? [target.orderRowId]
                        : target.kind === 'orders' ? target.orderRowIds : undefined,
                    isCompleted: field === 'isCompleted' ? value : undefined,
                    isSentMq: field === 'isSentMq' ? value : undefined,
                    retryCount: field === 'retryCount' ? value : undefined,
                },
            },
        });
        const payload = result.data?.opsToolBatchUpdateDispatchEventTaskOrderStatus;
        if (!payload) return;
        if (!payload.success) {
            message.error(payload.message);
            return;
        }
        message.success(`更新成功，生效 ${payload.updatedCount ?? 0} 条订单明细`);
        close();
        onSuccess?.();
    };

    const handleOk = () => {
        if (!target) return;
        if (updateField === undefined || targetValue === undefined) {
            message.warning('请选择更新字段和目标值');
            return;
        }
        const fieldLabel = updateField === 'isCompleted'
            ? 'is_completed'
            : updateField === 'isSentMq' ? 'is_sent_mq' : 'retry_count';
        Modal.confirm({
            title: '风险操作确认',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: '确认更新',
            okButtonProps: { danger: true },
            content: `即将把${scopeText}的 ${fieldLabel} 更新为 ${targetValue}，该操作直接修改执行状态、不可自动回滚，确认继续？`,
            onOk: () => doUpdate(updateField, targetValue),
        });
    };

    return (
        <Modal
            title={titleText}
            open={target !== null}
            confirmLoading={loading}
            onOk={handleOk}
            onCancel={close}
            destroyOnClose
        >
            <div className="mb-3 text-gray-500">
                将{scopeText}（<span className="text-red-500 font-medium">tms_dispatch_event_task_order</span>
                ）的所选字段更新为目标值；仅更新字段，不会触发 MQ 重发
                {target?.kind === 'task' && '；更新完成状态时，调度任务自身的完成状态会同步为相同值'}
            </div>
            <div className="flex flex-col gap-3">
                <Select
                    className="w-full"
                    placeholder="选择更新字段"
                    options={ORDER_UPDATE_FIELD_OPTIONS}
                    value={updateField}
                    onChange={(v) => {
                        setUpdateField(v);
                        // 字段切换后目标值取值范围不同，清空重选
                        setTargetValue(undefined);
                    }}
                />
                <Select
                    className="w-full"
                    placeholder="选择目标值"
                    options={updateField === 'retryCount' ? RETRY_VALUE_OPTIONS : FLAG_OPTIONS}
                    value={targetValue}
                    onChange={setTargetValue}
                />
            </div>
        </Modal>
    );
};

export default OrderFieldUpdateModal;
